/**
 * AI 권한·월 크레딧(클라이언트).
 * - 서버(api/_lib/aiAccess.cjs)가 최종 판정. 여기는 버튼 노출·잔여 표시용.
 * - 실패 안전: RPC 를 못 읽으면 관리자 이메일만 허용(무제한), 그 외는 거절로 본다.
 * DB: supabase/migrations/20261026190000_plan_permissions_ai_quota.sql (cp_ai_my_quota)
 */
import { supabase } from '../supabaseClient';
import { isPlatformAdminEmail } from '../constants/platformAdmin';
import { PLAN_AI_FEATURE_KEYS, type PlanAiFeatureKey } from '../constants/planFeatures';

export type AiFeature = 'learning_plan' | 'worksheet' | 'slides' | 'other';

export const AI_FEATURES: AiFeature[] = ['learning_plan', 'worksheet', 'slides', 'other'];

export type AiQuota = {
  isAdmin: boolean;
  unlimited: boolean;
  plan: string | null;
  permissions: Record<AiFeature, boolean>;
  /**
   * AI 세부 기능(관리자 「이용권별 권한 관리」 항목: 교육과정 자료 입력·평가·행동특성 피드백).
   * 서버 aiFeatureAllowed 와 같은 규칙: extra.features.ai_* 값, 없으면(마이그레이션·저장 전) 예전 ai_other 열.
   */
  planFeatures: Record<PlanAiFeatureKey, boolean>;
  granted: number;
  used: number;
  remaining: number;
  /** 'YYYY-MM' (KST) */
  period: string;
  /** 다음 초기화 날짜 'YYYY-MM-DD' (KST 1일 0시) */
  resetAt: string;
  maxClasses: number | null;
};

export const AI_QUOTA_CHANGED_EVENT = 'cp-ai-quota-changed';

const NONE: Record<AiFeature, boolean> = { learning_plan: false, worksheet: false, slides: false, other: false };
const ALL: Record<AiFeature, boolean> = { learning_plan: true, worksheet: true, slides: true, other: true };
const planFeaturesAll = (on: boolean): Record<PlanAiFeatureKey, boolean> =>
  Object.fromEntries(PLAN_AI_FEATURE_KEYS.map((k) => [k, on])) as Record<PlanAiFeatureKey, boolean>;

function num(v: unknown): number {
  const x = Number(v);
  return Number.isFinite(x) && x > 0 ? x : 0;
}

/** KST 기준 이번 달·다음 초기화일(서버를 못 읽을 때 표시용) */
export function kstPeriodNow(now: Date = new Date()): { period: string; resetAt: string } {
  const k = new Date(now.getTime() + 9 * 3600 * 1000);
  const y = k.getUTCFullYear();
  const m = k.getUTCMonth() + 1;
  const ny = m === 12 ? y + 1 : y;
  const nm = m === 12 ? 1 : m + 1;
  return { period: `${y}-${String(m).padStart(2, '0')}`, resetAt: `${ny}-${String(nm).padStart(2, '0')}-01` };
}

export function adminUnlimitedQuota(): AiQuota {
  const p = kstPeriodNow();
  return { isAdmin: true, unlimited: true, plan: null, permissions: { ...ALL }, planFeatures: planFeaturesAll(true), granted: 0, used: 0, remaining: 0, period: p.period, resetAt: p.resetAt, maxClasses: null };
}

export function deniedQuota(): AiQuota {
  const p = kstPeriodNow();
  return { isAdmin: false, unlimited: false, plan: null, permissions: { ...NONE }, planFeatures: planFeaturesAll(false), granted: 0, used: 0, remaining: 0, period: p.period, resetAt: p.resetAt, maxClasses: null };
}

/** cp_ai_my_quota(jsonb, snake_case) → AiQuota */
export function normalizeQuotaRow(raw: unknown): AiQuota | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const perms = (r.permissions && typeof r.permissions === 'object' ? r.permissions : {}) as Record<string, unknown>;
  const isAdmin = Boolean(r.is_admin);
  const featureFlags = (r.features && typeof r.features === 'object' ? r.features : {}) as Record<string, unknown>;
  const planFeatures = planFeaturesAll(false);
  for (const k of PLAN_AI_FEATURE_KEYS) {
    const v = featureFlags[k];
    planFeatures[k] = isAdmin || (typeof v === 'boolean' ? v : perms.other === true);
  }
  const p = kstPeriodNow();
  const granted = num(r.granted);
  const used = num(r.used);
  return {
    isAdmin,
    unlimited: isAdmin || Boolean(r.unlimited),
    plan: typeof r.plan_key === 'string' ? r.plan_key : null,
    permissions: {
      learning_plan: isAdmin || perms.learning_plan === true,
      worksheet: isAdmin || perms.worksheet === true,
      slides: isAdmin || perms.slides === true,
      other: isAdmin || perms.other === true,
    },
    planFeatures,
    granted,
    used: Math.round(used * 10) / 10,
    remaining: Math.max(0, Math.floor((granted - used) * 10) / 10),
    period: typeof r.period === 'string' ? r.period : p.period,
    resetAt: typeof r.reset_at === 'string' ? r.reset_at : p.resetAt,
    maxClasses: Number.isFinite(Number(r.max_classes)) && Number(r.max_classes) > 0 ? Number(r.max_classes) : null,
  };
}

/** 내 권한·잔여. 실패 시 관리자는 무제한, 그 외는 거절 */
export async function fetchMyAiQuota(): Promise<AiQuota | null> {
  const { data: sess } = await supabase.auth.getSession();
  const email = sess.session?.user?.email ?? '';
  if (!sess.session) return null;
  if (isPlatformAdminEmail(email)) {
    // 관리자: 항상 무제한. RPC 를 읽을 수 있으면 이번 달 사용량만 표시용으로 가져온다.
    try {
      const { data, error } = await supabase.rpc('cp_ai_my_quota');
      const q = error ? null : normalizeQuotaRow(data);
      return q ? { ...adminUnlimitedQuota(), used: q.used, period: q.period, resetAt: q.resetAt, maxClasses: q.maxClasses } : adminUnlimitedQuota();
    } catch {
      return adminUnlimitedQuota();
    }
  }
  try {
    const { data, error } = await supabase.rpc('cp_ai_my_quota');
    if (error) throw error;
    return normalizeQuotaRow(data) ?? deniedQuota();
  } catch {
    return deniedQuota();
  }
}

/** API 응답의 aiQuota(서버 camelCase, 권한 없음)를 알림 → useAiAccess 가 숫자만 갱신 */
export function notifyAiQuotaChanged(serverQuota?: unknown): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(AI_QUOTA_CHANGED_EVENT, { detail: serverQuota ?? null }));
}

/** 'YYYY-MM-DD' → '11월 1일' */
export function formatResetDate(resetAt: string | undefined | null): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(resetAt || '')) ?? /^(\d{4})-(\d{2})-(\d{2})/.exec(kstPeriodNow().resetAt);
  if (!m) return '';
  return `${Number(m[2])}월 ${Number(m[3])}일`;
}

export function formatCredits(n: number): string {
  return Math.floor(n).toLocaleString('ko-KR');
}

/** AI 응답(성공·실패 모두)에 실린 잔여(aiQuota / quota)를 화면 배지에 반영 */
export function syncAiQuotaFromResponse(json: unknown): void {
  if (!json || typeof json !== 'object') return;
  const j = json as { aiQuota?: unknown; quota?: unknown };
  const q = j.aiQuota ?? j.quota;
  if (q && typeof q === 'object') notifyAiQuotaChanged(q);
}

/**
 * 권한/한도 오류 코드 → 안내 문구(해당 없으면 null).
 */
export function aiAccessErrorMessage(code: string | undefined | null, json?: unknown): string | null {
  const body = (json && typeof json === 'object' ? json : {}) as { quota?: { resetAt?: string }; reason?: string };
  if (code === 'AI_QUOTA_EXCEEDED') {
    const date = formatResetDate(body.quota?.resetAt);
    return `이번 달 AI 크레딧을 모두 사용했어요. ${date} 0시(한국 시간)에 다시 채워집니다.`;
  }
  if (code === 'AI_NOT_ALLOWED') {
    if (body.reason === 'ACCESS_UNAVAILABLE') return 'AI 이용 권한을 확인하지 못했습니다. 잠시 후 다시 시도해 주세요.';
    return '현재 이용권에서는 이 AI 기능을 사용할 수 없습니다.';
  }
  return null;
}
